/**
 * Labstep
 *
 * @module components/EntityRelation/SearchSelect
 * @desc SearchSelect for EntityRelations
 */

import { useGridNewIndexServiceFromContext } from 'labstep-web/contexts/grid/hook';
import { Checkbox } from 'labstep-web/core/Checkbox';
import { Flex } from 'labstep-web/core/Flex';
import { Icon } from 'labstep-web/core/Icon';
import { FloatingSearchSelect } from 'labstep-web/core/Select/FloatingSearch';
import { useActiveGroup } from 'labstep-web/hooks/activeGroup';
import { EntityRelation } from 'labstep-web/models/entity-relation.model';
import type { Resource } from 'labstep-web/models/resource.model';
import { generateNewDateString } from 'labstep-web/services/date.service';
import {
  createEntity,
  updateEntity,
} from 'labstep-web/state/actions/entity';
import store from 'labstep-web/state/store';
import { useState } from 'react';
import type { EntityRelationSearchSelectProps } from './types';

interface LinkedResource {
  id: number;
  name: string;
  isLinked: boolean;
  relationGuid: string;
}

export const EntityRelationSearchSelect: React.FC<
  EntityRelationSearchSelectProps
> = ({
  metadata,
  sourceEntity,
  createEntityRelation,
  updateEntityRelation,
  stopEdit,
}) => {
  const { activeGroup } = useActiveGroup();
  const ctx = useGridNewIndexServiceFromContext();

  const [optionsSelected, setOptionsSelected] = useState<
    LinkedResource[] | null
  >(null);

  const [removedRelations, setRemovedRelations] = useState<
    LinkedResource[]
  >([]);

  const fieldDefinition = metadata.field_definition;
  if (!fieldDefinition) {
    return null;
  }

  if (!fieldDefinition.is_multiple_entity_relations_allowed) {
    return (
      <FloatingSearchSelect
        actionComponentProps={{
          type: 'children',
          children: () => <div></div>,
        }}
        entityName={sourceEntity.entityName}
        placeholder={`Search ${sourceEntity.entityName}s...`}
        keepMenuOpen
        autoFocus
        creatable
        createBody={{
          ...fieldDefinition.entity_relation_target_entity_filter,
        }}
        open={true}
        params={{
          ...fieldDefinition.entity_relation_target_entity_filter,
          group_id: activeGroup!.id,
          count: 20,
        }}
        formatOptionLabel={(option: any) => {
          const linkedResourcesIds = (optionsSelected || []).map(
            (resource: LinkedResource) => resource.id,
          );
          return (
            <Flex spacing="between" vAlignContent="center">
              <span>{option.displayName}</span>
              {linkedResourcesIds.includes(option.id) && (
                <Icon name="close" />
              )}
            </Flex>
          );
        }}
        setOptions={(options: Resource[]) => {
          const linkedResources =
            metadata.entity_relation_preview.map(
              (entityRelation: EntityRelation) => {
                const resource =
                  entityRelation.source?.guid === sourceEntity.guid
                    ? entityRelation.target
                    : entityRelation.source;
                return {
                  id: resource?.id,
                  name: resource?.displayName,
                  isLinked: true,
                  relationGuid: entityRelation.guid,
                } as LinkedResource;
              },
            );
          if (optionsSelected === null) {
            setOptionsSelected(linkedResources);
          }

          const linkedResourcesIds = linkedResources.map(
            (resource: LinkedResource) => resource.id,
          );
          return [
            {
              label: 'Linked Resource',
              options: options.filter((option) =>
                linkedResourcesIds.includes(option.id),
              ),
            },
            {
              label: 'Other Resources',
              options: options.filter(
                (option) => !linkedResourcesIds.includes(option.id),
              ),
            },
          ];
        }}
        onChange={(option) => {
          const rels = metadata.entity_relation_preview;
          const isSelected = rels.some(
            (r: EntityRelation) =>
              r.target_resource.guid === option.guid ||
              r.source_resource.guid === option.guid,
          );

          const makePayload = () => ({
            [`source_${fieldDefinition!.entity_relation_target_entity_class}_id`]:
              sourceEntity.guid,
            [`target_${fieldDefinition!.entity_relation_target_entity_class}_id`]:
              option.guid,
            field_definition_id: fieldDefinition.id,
          });

          const onSuccess = () => {
            stopEdit();
          };

          if (isSelected) {
            store.dispatch(
              updateEntity(
                EntityRelation.entityName,
                option.guid,
                { deleted_at: generateNewDateString() },
                { onSuccess },
              ),
            );
          } else if (
            !fieldDefinition.is_multiple_entity_relations_allowed &&
            rels.length > 0
          ) {
            store.dispatch(
              updateEntity(
                EntityRelation.entityName,
                rels[0].guid,
                { deleted_at: generateNewDateString() },
                { onSuccess },
              ),
            );
            store.dispatch(
              createEntity(
                EntityRelation.entityName,
                makePayload(),
                undefined,
                undefined,
                undefined,
                { onSuccess },
              ),
            );
          } else {
            store.dispatch(
              createEntity(
                EntityRelation.entityName,
                makePayload(),
                undefined,
                undefined,
                undefined,
                { onSuccess },
              ),
            );
          }

          stopEdit();
        }}
      />
    );
  }

  return (
    <FloatingSearchSelect
      actionComponentProps={{
        type: 'children',
        children: () => <div></div>,
      }}
      entityName={sourceEntity.entityName}
      placeholder={`Search ${sourceEntity.entityName}...`}
      keepMenuOpen
      autoFocus
      creatable
      createBody={{
        ...fieldDefinition.entity_relation_target_entity_filter,
      }}
      open={true}
      params={{
        ...fieldDefinition.entity_relation_target_entity_filter,
        group_id: activeGroup!.id,
        count: 20,
      }}
      onClose={() => {
        if (!optionsSelected) {
          return;
        }
        const itemsToCreate = optionsSelected
          .filter((relation) => !relation.relationGuid)
          .map((option: any) => ({
            [`source_${fieldDefinition!.entity_relation_target_entity_class}_id`]:
              sourceEntity.guid,
            [`target_${fieldDefinition!.entity_relation_target_entity_class}_id`]:
              option.guid,
            field_definition_id: fieldDefinition.id,
          }));
        if (removedRelations.length > 0 && updateEntityRelation) {
          const removedRelationsPayload = removedRelations.map(
            (relation: any) => ({
              id: relation.relationGuid,
              deleted_at: generateNewDateString(),
            }),
          );
          updateEntityRelation(removedRelationsPayload as any);
        }
        if (itemsToCreate.length > 0) {
          createEntityRelation(itemsToCreate as any);
        }

        if (ctx) {
          ctx.agGrid.api.refreshCells({
            force: true,
            suppressFlash: true,
          });
        }
        stopEdit();
      }}
      setOptions={(options: Resource[]) => {
        const linkedResources = metadata.entity_relation_preview.map(
          (entityRelation: EntityRelation) => {
            const resource =
              entityRelation.source?.guid === sourceEntity.guid
                ? entityRelation.target
                : entityRelation.source;
            return {
              id: resource?.id,
              name: resource?.displayName,
              isLinked: true,
              relationGuid: entityRelation.guid,
            } as LinkedResource;
          },
        );

        if (optionsSelected === null) {
          setOptionsSelected(linkedResources);
        }

        const linkedResourcesIds = optionsSelected
          ? optionsSelected.map(
              (resource: LinkedResource) => resource.id,
            )
          : linkedResources.map(
              (resource: LinkedResource) => resource.id,
            );

        const otherResources = options
          .filter((option) => !linkedResourcesIds.includes(option.id))
          .map((option: Resource) => ({
            ...option,
            name: option.displayName,
          }));
        return [
          {
            label: 'Linked Resources',
            options: optionsSelected
              ? optionsSelected
              : linkedResources,
          },
          {
            label: 'Link Other Resources',
            options: otherResources,
          },
        ];
      }}
      formatOptionLabel={(option: any) => {
        const linkedResourcesIds = (optionsSelected || []).map(
          (resource: LinkedResource) => resource.id,
        );
        return (
          <Flex
            spacing="gap"
            onClick={(e) => {
              e.preventDefault();
              if (linkedResourcesIds.includes(option.id)) {
                setOptionsSelected(
                  (optionsSelected || []).filter(
                    (o) => o.id !== option.id,
                  ),
                );
              }
            }}
            data-cy={`components/EntityRelation/SearchSelect:Checkbox-${linkedResourcesIds.includes(option.id) ? 'checked' : 'unchecked'}`}
          >
            <Checkbox
              disabled={true}
              checked={linkedResourcesIds.includes(option.id)}
              onClick={(e) => {
                if (linkedResourcesIds.includes(option.id)) {
                  setOptionsSelected(
                    (optionsSelected || []).filter(
                      (o) => o.id !== option.id,
                    ),
                  );
                }
              }}
            />
            <span>{option.name}</span>
          </Flex>
        );
      }}
      onBlur={() => {
        if (!optionsSelected) {
          return;
        }
        const itemsToCreate = optionsSelected
          .filter((relation) => !relation.relationGuid)
          .map((option: any) => ({
            [`source_${fieldDefinition!.entity_relation_target_entity_class}_id`]:
              sourceEntity.guid,
            [`target_${fieldDefinition!.entity_relation_target_entity_class}_id`]:
              option.guid,
            field_definition_id: fieldDefinition.id,
          }));
        if (removedRelations.length > 0 && updateEntityRelation) {
          const removedRelationsPayload = removedRelations.map(
            (relation: any) => ({
              id: relation.relationGuid,
              deleted_at: generateNewDateString(),
            }),
          );
          updateEntityRelation(removedRelationsPayload as any);
        }
        if (itemsToCreate.length > 0) {
          createEntityRelation(itemsToCreate as any);
        }

        if (ctx) {
          ctx.agGrid.api.refreshCells({
            force: true,
            suppressFlash: true,
          });
        }
        stopEdit();
      }}
      onChange={(option) => {
        if (option.relationGuid) {
          setRemovedRelations(
            removedRelations.find(
              (relation) => relation.id === option.id,
            )
              ? removedRelations.filter(
                  (relation) => relation.id !== option.id,
                )
              : [...removedRelations, option],
          );
        } else {
          setOptionsSelected(
            (optionsSelected || []).find(
              (relation) => relation.id === option.id,
            )
              ? (optionsSelected || []).filter(
                  (relation) => relation.id !== option.id,
                )
              : [...(optionsSelected || []), option],
          );
        }
      }}
    />
  );
};
